import { useContext, useMemo } from 'react';
import Attachments from 'src/components/Attachments';
import PropertyOwnerInformation from 'src/components/PropertyOwnerInfo/index';
import PropertyType from 'src/components/PropertyType';
import PropertyTypeInfo from 'src/components/PropertyTypeInfo';
import PropertyValueAndTaxInfo from 'src/components/PropertyValueAndTaxInfo';
import PropertyValueTaxModal from 'src/components/PropertyValueAndTaxInfo/PropertyValueTaxModal';
import Review from 'src/components/Review';
import StatementOfCircumstances from 'src/components/StatementOfCircumstances';
import Step from 'src/components/Stepper/Step';
import Confirmation from 'src/components/util/Confirmation';
import Page from 'src/components/util/Page';
import { PropValAndTaxInfoContextProvider } from 'src/contexts/prop-val-and-tax-info';
import FormContext from 'src/contexts/settlement-context';
import attachmentSchema from 'src/schemas/attachmentSchema';
import ownersStatementSchema from 'src/schemas/ownersStatement';
import propertyInformationSchema from 'src/schemas/propertyInformationSchema';
import propertyOwnerInformationSchema from 'src/schemas/propertyOwnerInformation';
import propertyTypeSchema from 'src/schemas/propertyType';
import propertyValueAndTaxInfoSchema from 'src/schemas/propertyValueAndTaxInfo';
import submitSchema from 'src/schemas/submitSchema';

/**
 * A multi-step form for submitting a property settlement. The form is composed of multiple `Step` components,
 * each of which corresponds to a specific step in the form. The `Step` component is responsible for rendering
 * a form field and handling form validation and submission.
 *
 * The `SettlementForm` component uses the `FormContext` to manage the state of the form and provide functions
 * for navigating between steps and submitting the form. It also uses the `PropValAndTaxInfoContextProvider`
 * to provide context for the `PropertyValueAndTaxInfo` component.
 *
 * @returns {JSX.Element} A multi-step form for submitting a property settlement
 */
export default function SettlementForm() {
  const {
    formData,
    isComplete,
    onBack,
    onNext,
    onSubmit,
    isLoading,
    contacts,
    disableNext,
    setDisableNext,
  } = useContext(FormContext);

  const formSteps = useMemo(
    () => [
      {
        component: <PropertyOwnerInformation setDisableNext={setDisableNext} />,
        label: 'Property Owner Information',
        schema: propertyOwnerInformationSchema,
        isFirstStep: true,
        initialValues: {
          owner: formData.owner,
        },
      },
      {
        component: <PropertyType />,
        label: 'Property Type',
        schema: propertyTypeSchema,
        initialValues: {
          type: formData.type,
        },
        isLastStep: false,
      },
      {
        component: <PropertyTypeInfo type={formData.type} />,
        label: 'Property Information',
        schema: propertyInformationSchema(formData.type),
        initialValues: {
          info: formData.info,
        },
        isLastStep: false,
      },
      {
        component: <PropertyValueAndTaxInfo type={formData.type} />,
        modals: [<PropertyValueTaxModal key={'propValModal'} />],
        label: 'Property Value and Tax Information',
        schema: propertyValueAndTaxInfoSchema,
        initialValues: {
          valueAndTaxInfo: formData.valueAndTaxInfo,
        },
        isLastStep: false,
      },
      {
        component: <StatementOfCircumstances />,
        label: 'Owners Statement of Circumstances',
        schema: ownersStatementSchema,
        initialValues: {
          statement: formData.statement,
        },
        isLastStep: false,
      },
      {
        component: <Attachments />,
        label: 'File Upload',
        schema: attachmentSchema,
        isLastStep: false,
      },
      {
        component: <Review />,
        label: 'Review',
        schema: submitSchema,
        isLastStep: true,
      },
    ],
    [formData]
  );

  if (isComplete) {
    return (
      <Confirmation
        text="Your Application for Settlement or Deferral of Delinquent Property Tax has successfully been submitted!"
        contacts={contacts}
      />
    );
  }

  return (
    <PropValAndTaxInfoContextProvider>
      <Page pageContext={FormContext}>
        {formSteps.map((step) => (
          <Step
            key={step.label}
            {...step}
            disableNext={disableNext}
            onBack={onBack}
            onNext={onNext}
            onSubmit={onSubmit}
            isLoading={isLoading}
          />
        ))}
      </Page>
    </PropValAndTaxInfoContextProvider>
  );
}
